import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { useAuth } from '../context/AuthContext';
import { listPurchases, type PurchaseListItem } from '../lib/api';
import { purchaseBillingLabelNl, purchaseBillingRevoked } from '../lib/purchaseBilling';

function formatDate(ts: number): string {
  try {
    return new Date(ts * 1000).toLocaleString('nl-NL');
  } catch {
    return String(ts);
  }
}

export function AccountPage() {
  const { user, token, loading } = useAuth();
  const [purchases, setPurchases] = useState<PurchaseListItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !user) return;
    let cancelled = false;
    listPurchases(token).then((r) => {
      if (cancelled) return;
      if (!r.ok) setErr('Kon aankopen niet laden.');
      else setPurchases(r.purchases);
    });
    return () => {
      cancelled = true;
    };
  }, [token, user]);

  if (!loading && !user) {
    return <Navigate to="/inloggen" replace />;
  }

  return (
    <>
      <Seo
        path="/account"
        title="Mijn account"
        description="ComfortTool™ account: overzicht van je aankopen, rapporten en downloads."
        keywords="ComfortTool account, aankopen, rapport"
        noindex
      />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-2xl font-bold text-ct-text">Mijn account</h1>
        <p className="mt-1 text-sm text-ct-muted">{user?.email}</p>
        <h2 className="mt-10 text-lg font-semibold text-ct-text">Aankopen</h2>
        {err && <p className="mt-2 text-sm text-red-400">{err}</p>}
        {!err && purchases && purchases.length === 0 && (
          <p className="mt-4 text-sm text-ct-muted">
            Nog geen aankopen gekoppeld. Na een betaling:{' '}
            <Link to="/bedankt" className="text-ct-accent hover:underline">
              bedankt-pagina
            </Link>{' '}
            opnieuw openen of{' '}
            <Link to="/inloggen" className="text-ct-accent hover:underline">
              inloggen
            </Link>{' '}
            met hetzelfde e-mailadres als bij je betaling.
          </p>
        )}
        {purchases && purchases.length > 0 && (
          <ul className="mt-4 space-y-3">
            {purchases.map((p) => (
              <li
                key={p.id}
                className="rounded-xl border border-ct-border bg-ct-surface px-4 py-3"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-medium text-ct-text">
                      {p.bundle_tier === 'basis' ? 'Comfort Basis' : 'Comfort Plus'} · {p.engine_version}
                    </p>
                    <p className="text-xs text-ct-muted">{formatDate(p.created_at)}</p>
                    <p className="mt-1.5">
                      <span
                        className={
                          purchaseBillingRevoked(p.billing_status ?? 'active')
                            ? 'rounded-full bg-red-500/15 px-2 py-0.5 text-xs font-medium text-red-300'
                            : (p.billing_status ?? 'active') !== 'active'
                              ? 'rounded-full bg-ct-warn/15 px-2 py-0.5 text-xs font-medium text-ct-warn'
                              : 'rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-300'
                        }
                      >
                        {purchaseBillingLabelNl(p.billing_status ?? 'active')}
                      </span>
                    </p>
                  </div>
                  <Link
                    to={`/account/aankopen/${p.id}`}
                    className="rounded-lg bg-ct-accent px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-500"
                  >
                    Rapport &amp; details
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
